import { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useSession } from "@/auth/session";
import { listOperations } from "@/lib/api";

interface NavItem {
  label: string;
  icon: string;
  path: string;
  /** Uno o varios module codes — visible si tiene acceso a CUALQUIERA de ellos */
  moduleCode: string | string[];
  badge?: number;
}

interface NavGroup {
  group: string;
  items: NavItem[];
}

const navGroups: NavGroup[] = [
  {
    group: "Principal",
    items: [
      { label: "Dashboard", icon: "ri-dashboard-line", path: "/", moduleCode: "dashboard" },
    ],
  },
  {
    group: "Gestion",
    items: [
      { label: "Clientes", icon: "ri-user-line", path: "/clientes", moduleCode: "clientes" },
      { label: "Facturacion", icon: "ri-file-list-3-line", path: "/facturacion", moduleCode: "facturacion" },
      { label: "Registrar Pago", icon: "ri-money-dollar-circle-line", path: "/registrar-pago", moduleCode: "facturacion" },
      { label: "Operaciones", icon: "ri-tools-line", path: "/operaciones", moduleCode: "operaciones" },
      { label: "Gastos Variables", icon: "ri-price-tag-3-line", path: "/gastos-variables", moduleCode: "facturacion" },
    ],
  },
  {
    group: "Inventario",
    items: [
      { label: "Bodega", icon: "ri-store-2-line", path: "/bodega", moduleCode: "bodega" },
    ],
  },
  {
    group: "Control de lecturas",
    items: [
      { label: "Lecturas", icon: "ri-book-open-line", path: "/lecturas", moduleCode: "control_lecturas" },
    ],
  },
  {
    group: "Administracion",
    items: [
      { label: "Administracion", icon: "ri-briefcase-line", path: "/administracion", moduleCode: ["facturacion", "prestamos", "documentacion_apr"] },
    ],
  },
  {
    group: "Analisis",
    items: [
      { label: "Reportes", icon: "ri-bar-chart-2-line", path: "/reportes", moduleCode: "reportes" },
      { label: "Auditoría", icon: "ri-shield-check-line", path: "/auditoria", moduleCode: "reportes" },
    ],
  },
  {
    group: "Otros",
    items: [{ label: "Configuracion", icon: "ri-settings-3-line", path: "/configuracion", moduleCode: "configuracion" }],
  },
];

interface SidebarProps {
  collapsed: boolean;
}

export default function Sidebar({ collapsed }: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { hasModule, profile, session, companyName } = useSession();
  const [operationsBadge, setOperationsBadge] = useState(0);

  useEffect(() => {
    let cancelled = false;

    async function loadOperationsBadge(): Promise<void> {
      if (!session || !hasModule("operaciones")) {
        if (!cancelled) setOperationsBadge(0);
        return;
      }

      try {
        const operations = await listOperations(session);
        if (cancelled) return;
        const activeCount = operations.filter((operation) => operation.status !== "completed").length;
        setOperationsBadge(activeCount);
      } catch {
        if (!cancelled) setOperationsBadge(0);
      }
    }

    void loadOperationsBadge();
    const intervalId = window.setInterval(() => {
      void loadOperationsBadge();
    }, 60000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [hasModule, session]);

  const visibleGroups = useMemo(
    () =>
      navGroups
        .map((group) => ({
          ...group,
          items: group.items
            .map((item) =>
              item.path === "/operaciones"
                ? { ...item, badge: operationsBadge > 0 ? operationsBadge : undefined }
                : item,
            )
            .filter((item) =>
              Array.isArray(item.moduleCode)
                ? item.moduleCode.some((m) => hasModule(m))
                : hasModule(item.moduleCode),
            ),
        }))
        .filter((group) => group.items.length > 0),
    [hasModule, operationsBadge],
  );

  const defaultGroupState = useMemo(
    () => Object.fromEntries(visibleGroups.map((group) => [group.group, true])) as Record<string, boolean>,
    [visibleGroups],
  );

  const [groupOpen, setGroupOpen] = useState<Record<string, boolean>>(defaultGroupState);

  useEffect(() => {
    setGroupOpen((prev) => {
      const next = { ...defaultGroupState };
      for (const [key, value] of Object.entries(prev)) {
        if (Object.hasOwn(next, key)) next[key] = value;
      }
      return next;
    });
  }, [defaultGroupState]);

  useEffect(() => {
    const activeGroup = visibleGroups.find((group) =>
      group.items.some((item) => location.pathname === item.path || (item.path !== "/" && location.pathname.startsWith(item.path))),
    );
    if (!activeGroup) return;
    setGroupOpen((prev) => (prev[activeGroup.group] ? prev : { ...prev, [activeGroup.group]: true }));
  }, [location.pathname, visibleGroups]);

  function toggleGroup(groupName: string): void {
    setGroupOpen((prev) => ({ ...prev, [groupName]: !prev[groupName] }));
  }

  const userName = profile?.fullName ?? "Usuario APR";
  const userRole = toRoleLabel(profile?.roles?.[0]);
  const userInitials = getInitials(userName);
  const canAccessSettings = hasModule("configuracion");

  return (
    <aside
      className={`fixed left-0 top-0 z-30 flex h-full flex-col transition-all duration-300 ${collapsed ? "w-16" : "w-60"}`}
      style={{ background: "linear-gradient(180deg, #0a1f45 0%, #12366f 54%, #0d4d73 100%)", boxShadow: "18px 0 36px -26px rgba(8, 31, 69, .9)" }}
    >
      <div className={`flex items-center gap-3 border-b border-white/10 px-4 py-5 ${collapsed ? "justify-center" : ""}`}>
        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl border border-cyan-400/40 bg-cyan-400/20">
          <i className="ri-drop-fill text-lg text-cyan-300" />
        </div>
        {!collapsed && (
          <div>
            <div className="text-sm font-bold leading-tight text-white">{companyName ?? "APR"}</div>
            <div className="text-xs text-cyan-300/70">Agua Potable Rural</div>
          </div>
        )}
      </div>

      <nav className="app-sidebar-scroll flex-1 overflow-y-auto py-3">
        {visibleGroups.map((group) => (
          <div key={group.group} className="mb-1">
            {!collapsed && (
              <button
                type="button"
                onClick={() => toggleGroup(group.group)}
                className="flex w-full items-center justify-between px-4 py-2 text-xs font-semibold uppercase tracking-widest text-white/50 transition-colors hover:text-white/80"
              >
                <span>{group.group}</span>
                <i className={`${groupOpen[group.group] ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-sm`} />
              </button>
            )}
            {(collapsed || groupOpen[group.group]) &&
              group.items.map((item) => {
                const isActive = location.pathname === item.path || (item.path !== "/" && location.pathname.startsWith(item.path));
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    className={`group relative mx-2 mb-1 flex items-center gap-3 rounded-xl border px-3 py-2.5 transition-all duration-200 ${
                      isActive
                        ? "border-white/20 bg-white/16 text-white"
                        : "border-transparent text-white/70 hover:bg-white/10 hover:text-white"
                    } ${collapsed ? "justify-center" : ""}`}
                  >
                    <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center">
                      <i className={`${item.icon} text-lg`} />
                    </div>
                    {!collapsed && <span className="flex-1 whitespace-nowrap text-sm font-medium">{item.label}</span>}
                    {!collapsed && item.badge ? (
                      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs font-bold text-white">{item.badge}</span>
                    ) : null}
                    {collapsed && item.badge ? (
                      <span className="absolute right-1 top-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-xs font-bold text-white">
                        {item.badge}
                      </span>
                    ) : null}
                    {collapsed ? (
                      <div className="pointer-events-none absolute left-full ml-2 whitespace-nowrap rounded-md bg-gray-900 px-2 py-1 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100">
                        {item.label}
                      </div>
                    ) : null}
                  </NavLink>
                );
              })}
          </div>
        ))}
      </nav>

      <div className={`border-t border-white/10 p-3 ${collapsed ? "flex justify-center" : ""}`}>
        {collapsed ? (
          <div className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-cyan-500/30 text-xs font-bold text-cyan-200">
            {userInitials}
          </div>
        ) : (
          <div className="flex cursor-pointer items-center gap-3 rounded-lg p-2 px-1 transition-colors hover:bg-white/5">
            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-cyan-500/30 text-xs font-bold text-cyan-200">
              {userInitials}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-xs font-semibold text-white">{userName}</div>
              <div className="truncate text-xs text-white/40">{userRole}</div>
            </div>
            {canAccessSettings ? (
              <button
                onClick={() => navigate("/configuracion")}
                title="Ir a Configuración"
                className={`flex h-6 w-6 items-center justify-center rounded-md transition-colors ${
                  location.pathname === "/configuracion"
                    ? "bg-cyan-400/20 text-cyan-300"
                    : "text-white/40 hover:bg-white/10 hover:text-white"
                }`}
              >
                <i className="ri-settings-3-line text-sm" />
              </button>
            ) : null}
          </div>
        )}
      </div>
    </aside>
  );
}

function getInitials(fullName: string): string {
  const initials = fullName
    .split(" ")
    .map((value) => value.trim())
    .filter(Boolean)
    .slice(0, 2)
    .map((value) => value[0]?.toUpperCase() ?? "")
    .join("");
  return initials || "UA";
}

function toRoleLabel(roleCode?: string): string {
  const labels: Record<string, string> = {
    admin: "Administrador",
    operator: "Operador",
    cashier: "Cajero",
    billing: "Facturación",
    field_reader: "Lector de Terreno",
    readonly: "Solo Lectura",
  };
  if (!roleCode) return "Sin rol";
  return labels[roleCode] ?? roleCode;
}
